import type { ReactNode } from "react";
import { cn } from "@/lib/cn";
import { Group, Paper, Stack, Text, ThemeIcon } from "@/components/ui";

interface Props {
  icon: ReactNode;
  label: string;
  value: number;
  color: string;
  /** Texto opcional junto al número (ej. "Solicitudes de pago"). */
  unidad?: string;
  /** Resalta el borde del card · usado para indicar "atencion necesaria". */
  highlight?: boolean;
  /**
   * Si se pasa, el card se vuelve clickeable y actua como quick filter
   * sobre el listado. `active` indica si el filtro esta aplicado en este
   * momento (afecta el borde y el aria-pressed).
   */
  onClick?: () => void;
  active?: boolean;
}

/**
 * Card compacta para una estadistica numerica con icono.
 *
 * Cuando recibe `onClick`, se renderiza como `<button>` con cursor pointer.
 * El borde refleja tres niveles visuales:
 *   - activo    · border multiva (fuerte, TODOS los cards usan el mismo
 *     acento de marca al activarse, sin importar el color de su ícono, para
 *     que el estado seleccionado se vea igual en toda la fila)
 *   - highlight · border del color del card (suave) · indica atención
 *   - default   · border neutro
 */
export function MiniStat({ icon, label, value, color, unidad, highlight, onClick, active }: Props) {
  const content = (
    <Paper
      padding="md"
      className={cn("transition-colors duration-140", active && "bg-multiva-50/60 dark:bg-multiva-900/20")}
      style={{
        borderColor: active ? "var(--color-multiva-600)" : highlight ? `var(--color-${color}-300)` : undefined,
      }}
    >
      <Group gap="sm" wrap="nowrap">
        <ThemeIcon variant="filled" color={color} size="md">
          {icon}
        </ThemeIcon>
        <Stack gap={2} className="text-left">
          <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="text-[10px]">
            {label}
          </Text>
          <Group gap={6} align="baseline" wrap="nowrap">
            <p className="text-xl font-bold leading-none text-foreground">{value}</p>
            {unidad && (
              <Text size="xs" c="dimmed" tt="uppercase" fw={600} className="text-[10px]">
                {unidad}
              </Text>
            )}
          </Group>
        </Stack>
      </Group>
    </Paper>
  );

  if (!onClick) return content;

  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      aria-label={active ? `Filtro activo · ${label} · click para quitar` : `Filtrar por ${label}`}
      className="block w-full cursor-pointer rounded-md text-left"
    >
      {content}
    </button>
  );
}
